import { useEffect, useRef } from 'react';
import QRCode from 'qrcode';

interface Props {
  url: string;
  title?: string;
  onClose(): void;
}

/**
 * Full-screen QR overlay. Tap anywhere to dismiss. Used by booth + gallery
 * pages so a host can print or project the URL at an event.
 */
export function QrOverlay({ url, title, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    void QRCode.toCanvas(canvas, url, {
      errorCorrectionLevel: 'H',
      width: 720,
      margin: 2,
      color: { dark: '#0b0b0f', light: '#ffffff' },
    });
  }, [url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal
      aria-label="Booth QR code"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.92)',
        backdropFilter: 'blur(8px)',
        zIndex: 50,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          display: 'grid',
          gap: 16,
          justifyItems: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <h2 style={{ margin: 0, color: '#fff', fontSize: 20 }}>{title}</h2>
        )}
        <div
          style={{
            background: '#fff',
            padding: 24,
            borderRadius: 16,
            boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
          }}
        >
          <canvas ref={canvasRef} style={{ display: 'block', width: 'min(72vmin, 480px)', height: 'auto' }} />
        </div>
        <div
          style={{
            color: '#cfcadf',
            fontSize: 14,
            wordBreak: 'break-all',
            textAlign: 'center',
            maxWidth: 480,
          }}
        >
          {url}
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '10px 22px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(255,255,255,0.08)',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Close (Esc)
        </button>
      </div>
    </div>
  );
}
